import {
  firstMatch,
  forceExcludeIfEnrolledIn,
  type ForceVariantEnv,
} from './enrollment-shared';

type ExcludeVariants = -1 | -2 | -3;

/**
 * Returns the variant the user should be forced into for this experiment, or
 * `undefined` to fall through to the standard random weighted enrollment.
 *
 * May return:
 *   - a positive defined variant (e.g. `1`) — forced enrollment, e.g. for
 *     paid ad traffic identified via UTM params or for direct-organic
 *     traffic identified via the absence of UTM/referer.
 *   - one of the negative `ExcludeVariants` codes (-1, -2, -3) — opt-out.
 *     Used for analytics integrity, e.g. excluding embedded-webview users.
 *
 * The hook is consulted only for anonymous (pre-signup) users on both the
 * client and SSR enrollment paths. Authenticated users always read from the
 * backend (`/api/v2/customers/self/experiments/`), so `forceVariant` does
 * not apply to them. An existing cookie enrollment also wins over
 * `forceVariant` — once a user is enrolled, they stay enrolled (sticky).
 */
export type ForceVariantFn<V extends readonly number[]> = (
  env: ForceVariantEnv
) =>
  | V[number]
  | ExcludeVariants
  | undefined
  | Promise<V[number] | ExcludeVariants | undefined>;

type ExperimentDefinition<T extends number, V extends readonly number[]> = {
  /**
   * Client-side identifier only. It is load-bearing for exactly two things:
   *
   *   1. The `useExperimentFlags` result key, built as `` `${name}Exp${id}` ``
   *      (see `hooks/useExperimentFlags.ts`) and its template-literal type
   *      (`types.ts`'s `GetVarKey`). Renaming `name` therefore renames a key
   *      that callers hold as a hardcoded string — every consumer must be
   *      updated in the same change.
   *   2. Display in the dev-mode debug panel, where it is also logged to
   *      Datadog as `experiment_name`.
   *
   * It is NOT part of any Segment payload, cookie value, localStorage key,
   * `?exp_NNN=` URL override, or backend request — all of those key off
   * `canonical`. So a rename is a frontend-only refactor: no backend
   * coordination and no analytics/enrollment continuity to preserve.
   */
  name: string;
  shortName: `exp_${T}`;
  canonical: T;
  variants: V;
  forceVariant?: ForceVariantFn<V>;
};

type ExperimentDefinitionMap<T extends number> = {
  [K in T]: ExperimentDefinition<K, readonly number[]>;
};

function defineExperiments<const D extends ExperimentDefinitionMap<number>>(
  defs: D
): D {
  return defs;
}

/**
 * "ExperimentDefinitions" contains definitions for all the experiments that
 * are currently in the web app.
 *
 * In order to use "useExperiment" / "useExperimentFlags", an experiment MUST
 * be defined here first.
 *
 * Use the optional `forceVariant` function to override the standard random
 * weighted enrollment for a pre-signup user. Common uses:
 *
 *   - Force paid ad traffic into a specific variant for clean attribution
 *     (return a positive variant when a UTM param matches).
 *   - Force direct-organic traffic into a specific variant (return a
 *     positive variant when no UTM is present and the referer is empty).
 *   - Opt the user out of the experiment for analytics integrity (return
 *     one of the negative `ExcludeVariants` codes -1, -2, -3 — e.g. -2 for
 *     embedded-webview users).
 *
 * `forceVariant(env)` receives:
 *   {
 *     isEmbeddedWebView: boolean;
 *     url: URL;
 *     utm: UtmDataType;            // pre-parsed UTM params
 *     referer: string | null;       // SSR: Referer header. Client: document.referrer
 *   }
 *
 * Return `undefined` (sync or via Promise) to skip the override and let the
 * regular enrollment algo run. Any non-undefined return wins.
 *
 * `forceVariant` is only consulted for anonymous (pre-signup) users; for
 * authenticated users the backend is the source of truth. An existing
 * cookie enrollment also wins over `forceVariant` (sticky).
 *
 * The "name" field is a camelCase descriptor used by useExperimentFlags to
 * generate the result key as `{name}Exp{id}` (e.g., name: 'aiLoadingScreen'
 * on experiment 213 becomes `aiLoadingScreenExp213` in the hook result).
 */

export const ExperimentDefinitions = defineExperiments({
  164: {
    // NO LONGER RUNNING: single variant live per experiment dashboard (variant 0 / 100). Candidate for removal.
    name: 'flexCreditsDefault', // FlexCredits Default for New Users - will be part of Single Price Per Credit enrollment
    shortName: 'exp_164',
    canonical: 164,
    variants: [0, 1],
  },

  165: {
    name: 'singlePricePerCredit', // Single Price Per Credit - in planning
    shortName: 'exp_165',
    canonical: 165,
    variants: [0, 1],
  },

  173: {
    // NO LONGER RUNNING: single variant live per experiment dashboard (variant 0). Do NOT remove without data migration
    name: 'payAsYouGo', // PAYG - waiting for discussion with CPO
    shortName: 'exp_173',
    canonical: 173,
    variants: [0, 1],
  },

  178: {
    name: 'biweeklyCadencePostCheckout', // Bi-weekly Cadence (post-checkout) - still data maturing
    shortName: 'exp_178',
    canonical: 178,
    variants: [0, 1],
  },

  181: {
    // NO LONGER RUNNING: single variant live per experiment dashboard (variant 0 / 100). Candidate for removal.
    name: 'ftcCancelFlow', // FTC cancel flow - waiting for 186 to run
    shortName: 'exp_181',
    canonical: 181,
    variants: [0, 1],
  },

  183: {
    // NO LONGER RUNNING: single variant live per experiment dashboard (variant 0 / 100). Candidate for removal.
    name: 'biweeklyCadencePreCheckout', // Pre-checkout bi-weekly cadence - still data maturing
    shortName: 'exp_183',
    canonical: 183,
    variants: [0, 1],
  },

  186: {
    // NO LONGER RUNNING: single variant live per experiment dashboard (variant 0 / 100). Candidate for removal.
    name: 'ftcRound3', // FTC round 3 - being removed (CA customers have variant 2 experience)
    shortName: 'exp_186',
    canonical: 186,
    variants: [0, 1, 2],
  },

  189: {
    // NO LONGER RUNNING: single variant live per experiment dashboard (variant 2 / 102). Candidate for removal.
    name: 'firstBoxFocus', // First box focus
    shortName: 'exp_189',
    canonical: 189,
    variants: [0, 1, 2],
  },

  195: {
    name: 'destinationFoodProfile', // Destination Food Profile
    shortName: 'exp_195',
    canonical: 195,
    // Part 2 relaunch arms (WEB-7763):
    //   10 = Part 2 control — same UX as variant 0. Clients read
    //        variant_modulo 0 (10 % 10), so it naturally matches control;
    //        do not gate destination surfaces on `isVariant10`.
    //   12 = ingredient-level Dislikes (MVP) — admin/Statsig label; clients
    //        read variant_modulo 2, and `isVariant12` is remapped to match
    //        that (see VARIANT_FLAG_EFFECTIVE_REMAP in useExperimentFlags).
    // Legacy Part 1 arms kept until migration:
    //   0 = unenrolled / classic control
    //   1 = destination Food Profile (Improve SmartCart hub, widgets)
    // Sparse on purpose. `isVariant` is true for ANY > 0, so destination Food
    // Profile surfaces must gate on `isVariant1` (never bare `isVariant`),
    // and Dislikes / Phase-1 hub on `isVariant12`.
    variants: [0, 1, 10, 12],
  },

  198: {
    // NO LONGER RUNNING: single variant live per experiment dashboard (variant 0 / 100). Needs migration discussion
    name: 'orderLevelIncentives', // Order level incentives
    shortName: 'exp_198',
    canonical: 198,
    variants: [0, 1, 2, 3],
  },

  220: {
    // Variant 0 is control (current signup experience).
    //
    // Exp 220 (Bento Box) — prepared meals at signup:
    //   - variant 1: backend favors food format selections (turned off)
    //   - variant 2: backend favors explicit selections (turned off)
    // Iteration 1 relaunch arms, so their data is separate from 0/1/2:
    //   - variant 10: iteration 1 control. Clients read variant_modulo 0
    //     (10 % 10), so it naturally matches control; do not gate on
    //     `isVariant10`.
    //   - variant 13: treatment kept only for meals-only customers. Clients
    //     read variant_modulo 3, and `isVariant13` is remapped to match that
    //     (see VARIANT_FLAG_EFFECTIVE_REMAP in useExperimentFlags).
    //   - variant 14: v14 treatment (food formats redesign, swipe deck
    //     filtering). Clients read variant_modulo 4, and `isVariant14` is
    //     remapped to match that (see VARIANT_FLAG_EFFECTIVE_REMAP).
    // In variant 13 the backend assigns pricing version 33 to customers who
    // pick only prepared meals on food formats and 32 (control) to everyone
    // else.
    //
    // Gate the quiz (food formats) on `bentoBoxExp220.isVariant`: a customer
    // has no plan, and so no pricing version, until the recommender runs.
    // Gate the plan UI after that on the plan's pricing version
    // (`BENTO_BOX_PRICING_VERSION`), never on enrollment. The Django admin
    // record must be `is_pre_signup = True` and `is_active` or anonymous
    // visitors never enroll (BE-8614). Cookie enrollment id is 22000
    // (canonical × 100). Client enrollment on `/quiz/*` via
    // `useFunnelRedirects` (WEB-7751).
    name: 'bentoBox',
    shortName: 'exp_220',
    canonical: 220,
    variants: [0, 1, 2, 10, 13, 14],
    // Mutually exclusive with Exp 255 (freemium mini-quiz).
    forceVariant: forceExcludeIfEnrolledIn(255, -2),
  },

  224: {
    name: 'cartExplainer', // Cart Explainer
    shortName: 'exp_224',
    canonical: 224,
    // Variants 1, 2 and 3 all render the same Carty search experience: the
    // Rooty overview carousel pinned to the top of the search results, with
    // the "Ask Rooty" conversation starters as a horizontal chip carousel
    // beneath it. They remain separate arms for enrollment and reporting;
    // only the rendering converged. Variant 0 is control (no Rooty).
    variants: [0, 1, 2, 3],
  },
  227: {
    name: 'progressiveOnboarding', // Progressive Onboarding
    shortName: 'exp_227',
    canonical: 227,
    variants: [0, 1, 2],
  },

  231: {
    // OpenSearch Migration. variant 0 (control) = existing Algolia search,
    // variant 1 = OpenSearch, variant 2 = Algolia (same as control) with an
    // artificial pre-request delay to isolate latency from result quality.
    name: 'openSearch',
    shortName: 'exp_231',
    canonical: 231,
    variants: [0, 1, 2],
  },

  233: {
    name: 'dynamicRecipes', // Dynamic Recipe RDPs
    shortName: 'exp_233',
    canonical: 233,
    variants: [0, 1],
  },

  234: {
    name: 'planRecommender', // Updates to the plan recommender based on Budget
    shortName: 'exp_234',
    canonical: 234,
    variants: [0, 1, 2, 3, 4, 5, 6, 7, 8],
  },

  235: {
    name: 'cancellationDeflection', // Cancellation Deflection
    shortName: 'exp_235',
    canonical: 235,
    variants: [0, 1],
  },

  242: {
    name: 'popsicle', // new bottom nav
    shortName: 'exp_242',
    canonical: 242,
    // Variant 1 = new-user onboarding. Variants 2 and 3 are both existing-user
    // onboarding and behave identically; 3 exists only so the FB community beta
    // cohort can be read separately from the original one-time v2 enrollment.
    // All treatment variants get the same shared popsicle experience; only the
    // onboarding entry differs, so non-onboarding code gates on `isVariant`.
    //
    // NOTE: the ones digit is the effective variant — raw values encode the
    // iteration in the tens digit and are reduced mod 10 (see
    // EXPERIMENT_ITERATION_MASK / `variant_modulo`). Variant 3 therefore claims
    // every value ending in 3, so a future iteration must not use 13/23/33.
    variants: [0, 1, 2, 3],
  },

  243: {
    name: 'stripePaymentUpdates', // Exp 243 — Stripe Payment Updates
    shortName: 'exp_243',
    canonical: 243,
    variants: [0, 1],
  },

  244: {
    // Test experiment used to validate client-side holdout_weight support.
    // Exists only in Staging; in Production `findExperimentDefinition`
    // returns null and the enrollment path falls back to control (0).
    name: 'holdoutWeightTest',
    shortName: 'exp_244',
    canonical: 244,
    variants: [0, 1],
  },

  245: {
    // Variant 1 applies reward credits automatically. Variant 2 is the
    // choose-and-spend arm. It is registered so `isVariant2` exists; the
    // spend UI is not built yet, and `isVariant` stays true for both arms.
    name: 'sprinkles', // Exp 245 — Rewards program (Sprinkles)
    shortName: 'exp_245',
    canonical: 245,
    variants: [0, 1, 2],
  },

  250: {
    // Pairing back-in-stock "Notify Me" on the pairing detail page. Gates the
    // frontend entry point only; delivery is separately gated backend-side by
    // the api.inventory_notifications.pairing_enabled Dynamic Config.
    name: 'pairingNotifyMe',
    shortName: 'exp_250',
    canonical: 250,
    variants: [0, 1],
  },

  252: {
    // Cookbooks 2.0 — customer-created cookbooks: multiple named cookbooks, a
    // the default "{Name}’s Saves" cookbook, and heart → Manage routing. Mobile web only.
    //
    // Nests inside Exp 242, which still owns the Cookbook tab and route this
    // lives in, so a customer needs the 242 treatment for any 252 surface to
    // be reachable. How the two gates compose is tracked on WEB-7515; this
    // definition intentionally gates nothing on its own yet.
    name: 'customerCookbooks',
    shortName: 'exp_252',
    canonical: 252,
    variants: [0, 1],
  },

  253: {
    // Rolled out: the explicit-selections quiz (the former variant 2 flow) is
    // now the only quiz, and nothing on the web side reads this flag. The
    // definition stays so quiz visitors keep getting a 253 enrollment row,
    // which the backend still depends on until its own cleanup lands —
    // notably Exp 254 (BE-8374) enrolls post-checkout only when a customer
    // is 253 control or unenrolled, so an unenrolled customer would be
    // handed the post-checkout deck the funnel already covered.
    //
    // Every visitor is forced into variant 2 so the stored arm matches the
    // UX they actually received. Enrollment happens on `/quiz/*` via
    // `useFunnelRedirects`, the same point as Exp 220. Remove this entry
    // together with the backend cleanup.
    name: 'planRecExplicitSelections',
    shortName: 'exp_253',
    canonical: 253,
    variants: [0, 1, 2, 3, 4],
    // Mutually exclusive with Exp 255 (freemium mini-quiz): a visitor already
    // enrolled in 255 is forced into the exclude arm, as before the rollout.
    forceVariant: firstMatch([forceExcludeIfEnrolledIn(255, -2), () => 2]),
  },

  254: {
    // Explicit selections in post-checkout onboarding, at
    // `/onboarding/improve-selections/`. Treatment sources the deck from the
    // consolidated v3 endpoint
    // (`getApiV3CustomerExplicitSelectionsRecommendations`), which returns one
    // flat, already-ordered list; control keeps the v2 lunch/dinner buckets and
    // the five-at-a-time `interleaveLunchDinner` staggering.
    //
    // The full quiz now collects explicit selections itself, so Welcome and
    // the attribution survey always skip this deck. Enrollment stays
    // backend-side (BE-8374). Never enroll client-side here.
    name: 'onboardingExplicitSelections',
    shortName: 'exp_254',
    canonical: 254,
    variants: [0, 1],
  },

  255: {
    // Pre-auth mini quiz at `/mini-quiz/*`. Variant 0 is control (redirect
    // home). Variants 1 and 2 are the flows labeled "Variant A" and
    // "Variant B" in Figma; code and Jira use 1/2 throughout.
    //
    // Sequence resolution lives in `resolveMiniQuizSequence` and must never
    // be added to `resolveQuizSequence` — a 255-enrolled visitor opening
    // `/quiz/intro/` would otherwise be redirected into `/mini-quiz/*`.
    //
    // The ones digit is the effective variant; the tens digit encodes
    // iteration (`EXPERIMENT_ITERATION_MASK`). Later iterations must avoid
    // 11/21/12/22 so they do not collide with these arms.
    //
    // The Django admin record must be `is_pre_signup = True` or anonymous
    // visitors never enroll and `/mini-quiz` is unreachable.
    name: 'miniQuiz',
    shortName: 'exp_255',
    canonical: 255,
    variants: [0, 1, 2],
    // Mutually exclusive with Exp 220 (Bento Box). A visitor already enrolled
    // in 220 is forced into the exclude arm here. See the mirror-image note
    // on 220 and `forceExcludeIfEnrolledIn`.
    forceVariant: forceExcludeIfEnrolledIn(220, -2),
  },

  256: {
    // Self-serve first-box cancels. Variant 0 = control (today's
    // `/cancel/first-order/` contact-care page). Variants 1 and 2 unlock the
    // treatment cancel flow for first-box customers, gated also by entry
    // source (Account Settings vs Zendesk `/cancel/?from=zendesk`).
    name: 'firstBoxSelfServeCancel',
    shortName: 'exp_256',
    canonical: 256,
    variants: [0, 1, 2],
  },

  259: {
    // Checkout SMS opt-in (TCPA compliance). Control keeps the pre-checked
    // checkbox and existing copy; treatment shows the updated consent copy and
    // leaves the box unchecked so the customer has to opt in themselves. No
    // error state if they continue without checking it. See
    // `getSmsOptInConfig` in `@hungryroot/pages-checkout`.
    //
    // Checkout is authenticated-only, so enrollment is entirely backend-side —
    // there is no pre-signup/client-side enrollment path for this experiment.
    name: 'checkoutSmsOptIn',
    shortName: 'exp_259',
    canonical: 259,
    variants: [0, 1],
  },

  260: {
    // Freemium re-engagement for EXISTING pre-checkout guests (mWeb, via
    // CRM). Variant 0 = control (today's guest experience). Variant 1 gives
    // the guest the same freemium chrome and gates as an Exp 255 treatment
    // guest: freemium guest surfaces treat "255 treatment OR 260 treatment"
    // as treatment, and "both control / one control + unenrolled" as control.
    //
    // Enrollment is entirely backend-side: a batch job enrolls customers
    // who already signed up but never checked out, excluding anyone already
    // enrolled in 255. There is no pre-signup/client-side enrollment path,
    // so this entry has no `forceVariant` and never appears in the
    // enrollment cookie. The Django admin record must NOT be
    // `is_pre_signup = True`, or anonymous visitors would start enrolling.
    //
    // The 255/260 overlap (a 255 enrollee the batch should have skipped)
    // is excluded from analysis, not enforced in code.
    name: 'guestFreemiumReengagement',
    shortName: 'exp_260',
    canonical: 260,
    variants: [0, 1],
  },

  266: {
    // Freemium checkout conversion for mini-quiz signups. Variant 0 =
    // control; variant 1 receives the same freemium re-engagement UI as Exp
    // 260 treatment once the visitor is an authenticated guest.
    //
    // Enrolls client-side on `/mini-quiz/loading/` (`useEnrollExp266`), the
    // last pre-auth screen, so the cookie assignment is persisted onto the
    // account by the signup POST. No pre-auth surface reads the arm; every
    // consumer gates on an authenticated guest and the backend assignment.
    // The loading route is only reachable in an Exp 255 treatment sequence,
    // so the population is exactly mini-quiz signups and no `forceVariant`
    // exclusion is needed (255 already excludes 220).
    //
    // The Django admin record must be `is_pre_signup = True` and `is_active`
    // or nobody enrolls (`experiments.enrollment.post-signup-experiment`).
    name: 'freemiumCheckoutConversion',
    shortName: 'exp_266',
    canonical: 266,
    variants: [0, 1],
  },

  267: {
    // Freemium hamburger navigation. Control keeps "How it works" as the last
    // link; variant 1 promotes it above "Discover".
    name: 'howItWorksNav',
    shortName: 'exp_267',
    canonical: 267,
    variants: [0, 1],
  },

  // Deliberately out of numeric sequence — see the comment below.
  900: {
    // Permanently reserved for the experiments package's OWN specs. This is
    // never a live experiment and must never be enrolled in production.
    //
    // `useExperimentFlags`/`GetVarKey` derive a spec's result type by
    // indexing this registry, so a generic "some experiment" fixture used
    // across `useExperimentFlags.spec.ts` and `enroll.spec.ts` needs a real
    // entry here to get a real inferred type — otherwise specs need an
    // `as Experiments` cast to fake membership in this union, which is what
    // this entry replaces. Retiring a real experiment (like 206, or the next
    // one) therefore never again forces those specs to repoint at a
    // different live id.
    //
    // Id 900 is chosen well outside the real registry's numeric range
    // (currently topping out in the 200s) so it can never collide with a
    // future real experiment.
    //
    // Inert in production for the same reason 244 above is: every enrollment
    // path — `findExperimentDefinition` (used by both the client-side and
    // SSR enrollment flows) and the authenticated read — resolves an
    // experiment only by matching a real backend record whose id falls in
    // this experiment's `id * 100..id * 100 + 99` range (90000-90099 here).
    // No backend experiment will ever be registered in that range, so the
    // match is always `undefined`, `findExperimentDefinition` returns
    // `null`, and every path falls back to control (0) — nobody is ever
    // actually enrolled. Nothing here depends on `NODE_ENV`.
    //
    // Deliberately has NO `forceVariant`: `enroll.spec.ts` installs one at
    // runtime via `ExperimentDefinitions[900].forceVariant = ...` (restored
    // after each test) specifically to test the SSR forceVariant plumbing
    // without touching a real experiment's predicate. A built-in
    // `forceVariant` here would collide with those tests.
    name: 'reservedTestExperiment',
    shortName: 'exp_900',
    canonical: 900,
    variants: [0, 1],
  },

  901: {
    // Sparse-variant companion to 900 above, permanently reserved for the
    // experiments package's OWN specs. This is never a live experiment and
    // must never be enrolled in production.
    //
    // Its gappy `variants: [0, 2, 6, 7]` are load-bearing: they're the only
    // regression coverage for `VariantFlagKeys` emitting exactly one flag
    // key per DECLARED variant (skipping the gaps at 1, 3, 4, 5) and never
    // emitting a key for an undeclared variant number. 900's contiguous
    // `[0, 1]` can't exercise that path.
    //
    // Inert in production by the same argument as 900: `findExperimentDefinition`
    // only matches a real backend record whose id falls in this experiment's
    // `id * 100..id * 100 + 99` range (90100-90199 here), which is well
    // outside the real registry's range (currently topping out in the 200s),
    // so no backend experiment will ever land there, the match is always
    // `undefined`, `findExperimentDefinition` returns `null`, and every path
    // falls back to control (0).
    //
    // Unlike 900, this entry must NOT be used for `forceVariant` tests —
    // that coverage belongs to 900 only.
    name: 'reservedSparseVariantTestExperiment',
    shortName: 'exp_901',
    canonical: 901,
    variants: [0, 2, 6, 7],
  },
} as const satisfies ExperimentDefinitionMap<number>);

if (process.env.NODE_ENV !== 'production') {
  const seen = new Set<string>();
  for (const [id, def] of Object.entries(ExperimentDefinitions) as [
    string,
    { name: string },
  ][]) {
    if (seen.has(def.name)) {
      throw new Error(
        `Duplicate name "${def.name}" found in experiment ${id}. Each experiment must have a unique name.`
      );
    }
    seen.add(def.name);
  }
}

export type Experiments = keyof typeof ExperimentDefinitions;
export type DefinedExperiments = (typeof ExperimentDefinitions)[Experiments];
export type ExperimentVariants<D extends Experiments> =
  | (typeof ExperimentDefinitions)[D]['variants'][number]
  | ExcludeVariants;
